"use client";

/**
 * Renders a contact method the person does NOT own — their company's shared
 * front desk / role mailbox — as a working action plus a "Company line" badge.
 *
 * The badge is the whole point. A rep must never read a switchboard number as
 * someone's direct line, so an inherited method is marked everywhere it appears
 * (see CONTACT_METHOD_EXCLUSIVITY). It is also read-only here: the shared value
 * is edited on the COMPANY page, and writing it back onto the contact row would
 * re-create the single-owner collision the model forbids.
 *
 * Still fully actionable: the value opens Hub's native compose / SMS modal
 * (NATIVE_CONTACT_METHOD_LINKS — never `mailto:` / `tel:` / `sms:`), targeted at
 * the company's line anchor when it has one so the resulting activity is logged
 * as the company's, not as this person's.
 */

import React from "react";
import { Building2 } from "lucide-react";
import {
  COMPANY_LINE_BADGE_LABEL,
  inheritedMethodEntityContext,
  inheritedMethodTooltip,
  type InheritedContactMethod,
} from "@/lib/contacts/company-line/inherited-methods";
import type { ContactMethodEntityContext } from "@/lib/utils/crm/contact-method-context";
import { ContactMethodActions } from "./ContactMethodActions";

export interface InheritedContactMethodValueProps {
  method: InheritedContactMethod;
  /**
   * Context used when the company has no line anchor yet — the surface's own
   * entity context, i.e. exactly today's behaviour until an anchor exists.
   */
  fallbackContext?: ContactMethodEntityContext;
  className?: string;
}

export function InheritedContactMethodValue({
  method,
  fallbackContext,
  className,
}: InheritedContactMethodValueProps) {
  const anchorContext = inheritedMethodEntityContext(method);
  return (
    <span className="inline-flex items-center gap-1.5 min-w-0">
      <ContactMethodActions
        kind={method.kind}
        value={method.value}
        entityContext={anchorContext ?? fallbackContext}
        className={className}
      />
      <span
        title={inheritedMethodTooltip(method)}
        className="inline-flex flex-shrink-0 items-center gap-1 rounded-full border border-gray-300 dark:border-gray-600 px-1.5 py-px text-[10px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400"
      >
        <Building2 className="h-3 w-3" aria-hidden="true" />
        {COMPANY_LINE_BADGE_LABEL}
      </span>
    </span>
  );
}
